import { useState } from 'react';

import { relativeTime, fullTime } from '../lib/format';
import type { MessageSummary } from '../lib/api';

interface MessageListPaneProps {
  readonly address: string | null;
  readonly messages: readonly MessageSummary[];
  readonly selectedId: string | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly onSelect: (id: string) => void;
  readonly onLoadMore: () => void;
  readonly onDeleteInbox: (address: string) => void;
  readonly onBack: () => void;
}

export function MessageListPane({
  address,
  messages,
  selectedId,
  loading,
  error,
  hasMore,
  loadingMore,
  onSelect,
  onLoadMore,
  onDeleteInbox,
  onBack,
}: MessageListPaneProps) {
  const [copied, setCopied] = useState(false);

  if (!address) {
    return (
      <section className="pane pane--messages" aria-label="Messages">
        <div className="empty">
          <span className="empty__title">No address selected</span>
          <span className="empty__hint">Choose an address on the left to read its mail.</span>
        </div>
      </section>
    );
  }

  const copyAddress = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // The Clipboard API needs a secure context and a permission that can be
      // refused. The address is on screen and selectable either way, so a
      // failure here is not worth an error message.
    }
  };

  const confirmDelete = (): void => {
    if (window.confirm(`Delete ${address} and all of its mail? This cannot be undone.`)) {
      onDeleteInbox(address);
    }
  };

  return (
    <section className="pane pane--messages" aria-label={`Messages for ${address}`}>
      <header className="pane__header">
        <button className="btn btn--ghost back" type="button" onClick={onBack}>
          ← Addresses
        </button>

        <button
          className="btn btn--ghost"
          type="button"
          onClick={() => void copyAddress()}
          title="Copy address to clipboard"
          style={{ fontFamily: 'var(--mono)', fontSize: 12, minWidth: 0, flex: 1, justifyContent: 'flex-start' }}
        >
          <span
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {copied ? 'Copied' : address}
          </span>
        </button>

        <button
          className="btn btn--ghost btn--danger"
          type="button"
          onClick={confirmDelete}
          title={`Delete ${address}`}
        >
          Delete
        </button>
      </header>

      {error ? (
        <p className="alert alert--inline" role="alert">
          {error}
        </p>
      ) : null}

      <div className="pane__body">
        {loading ? (
          <div className="empty">
            <div className="spinner" role="status" aria-label="Loading messages" />
          </div>
        ) : null}

        {!loading && messages.length === 0 ? (
          <div className="empty">
            <span className="empty__title">Nothing here yet</span>
            <span className="empty__hint">
              Mail sent to this address shows up within a few seconds of arriving.
            </span>
          </div>
        ) : null}

        {messages.map((message) => (
          <button
            key={message.id}
            className={`message-row${message.seen ? '' : ' message-row--unread'}`}
            type="button"
            aria-current={message.id === selectedId}
            onClick={() => onSelect(message.id)}
          >
            <div className="message-row__top">
              {!message.seen ? <span className="dot" aria-label="Unread" /> : null}

              <span className="message-row__peer">
                {message.direction === 'out' ? `To ${message.peerAddress}` : message.peerName ?? message.peerAddress}
              </span>

              {message.hasAttachments ? <span className="tag">file</span> : null}
              {message.direction === 'out' ? <span className="tag">sent</span> : null}

              <time className="message-row__time" dateTime={new Date(message.createdAt).toISOString()} title={fullTime(message.createdAt)}>
                {relativeTime(message.createdAt)}
              </time>
            </div>

            <div className="message-row__subject">{message.subject || '(no subject)'}</div>
            <div className="message-row__preview">{message.preview}</div>
          </button>
        ))}

        {hasMore ? (
          <div style={{ padding: 12 }}>
            <button
              className="btn btn--block"
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Load older'}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
